import axios from "axios";
import ReusableTable from "../ReusableTable/ReusableTable";
import classes from "./LabsProfiles.module.css";
import { env } from "../../constants/env";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { db } from "../../utils/offlineDB";

const LabsProfiles = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const userRole = user?.role;
  const [labData, setLabData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLabs = async () => {
    setLoading(true);

    try {
      // 🔹 1. Load local data first (offline-first)
      let localLabs = await db.offlineLabs.toArray();

      // Filter out soft-deleted labs
      localLabs = localLabs.filter((lab) => !lab._deleted);

      // 🔹 Filter for ASSESSOR only
      if (userRole === "ASSESSOR") {
        localLabs = localLabs.filter((lab) =>
          user.accessibleLabs?.includes(lab.id)
        );
      }

      if (localLabs.length > 0) {
        console.log("Loaded labs from local cache");
        setLabData(
          localLabs.map((lab) => ({
            dateTime: lab.createdAt
              ? new Date(lab.createdAt).toLocaleString()
              : "",
            labName: lab.labName,
            license: lab.hpczLicense,
            address: lab.physicalAddress || "",
            phone: lab.telephone,
            email: lab.email,
            id: lab.id,
          }))
        );
      }

      // 🔹 2. Offline → stop here
      if (!navigator.onLine) return;

      // 🔹 3. Fetch from server
      let serverLabs = (
        await axios.get(`${env.kataApiUrl}/api/labs`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ).data;

      // 🔹 Filter for ASSESSOR only
      if (userRole === "ASSESSOR") {
        serverLabs = serverLabs.filter((lab) =>
          user.accessibleLabs?.includes(lab._id)
        );
      }

      // 🔹 4. Merge server → local (without overwriting offline edits)
      for (const lab of serverLabs) {
        const localLab = await db.offlineLabs.get(lab._id);
        const hasOfflineEdits =
          localLab?.offline &&
          (!localLab.lastSynced ||
            localLab.timestamp > new Date(localLab.lastSynced).getTime());

        if (hasOfflineEdits) {
          console.log(
            `[fetchLabs] Skipping ${lab.labName} — offline edits exist`
          );
          continue;
        }

        await db.offlineLabs.put({
          ...lab,
          id: lab._id,
          offline: false,
          lastSynced: new Date().toISOString(),
          timestamp: Date.now(),
        });
      }

      // 🔹 5. Refresh UI from merged local DB
      let mergedLabs = await db.offlineLabs.toArray();
      mergedLabs = mergedLabs.filter((lab) => !lab._deleted);

      // 🔹 Filter ASSESSOR accessible labs
      if (userRole === "ASSESSOR") {
        mergedLabs = mergedLabs.filter((lab) =>
          user.accessibleLabs?.includes(lab.id)
        );
      }

      setLabData(
        mergedLabs.map((lab) => ({
          dateTime: lab.createdAt
            ? new Date(lab.createdAt).toLocaleString()
            : "",
          labName: lab.labName,
          license: lab.hpczLicense,
          address: lab.physicalAddress || "",
          phone: lab.telephone,
          email: lab.email,
          id: lab.id,
        }))
      );
    } catch (err) {
      console.error("Error fetching labs:", err);
      toast.error("Failed to fetch labs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabs();
  }, []);

  // ✅ Delete lab function
  const handleDelete = async (lab) => {
    if (!window.confirm(`Are you sure you want to delete ${lab.labName}?`))
      return;

    try {
      if (!navigator.onLine) {
        // Queue delete request for later
        await db.offlineLabs.add({
          url: `${env.kataApiUrl}/api/profile/${lab.id}`,
          method: "DELETE",
          body: null,
          token,
          timestamp: Date.now(),
        });

        // Remove locally
        await db.offlineLabs.delete(lab.id);
        setLabData((prev) => prev.filter((l) => l.id !== lab.id));

        toast.info(`${lab.labName} deleted locally (offline)`);
      } else {
        await axios.delete(`${env.kataApiUrl}/api/profile/${lab.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await db.offlineLabs.delete(lab.id); // update local cache
        setLabData((prev) => prev.filter((l) => l.id !== lab.id));
        toast.success(`Successfully deleted ${lab.labName}`);
      }
    } catch (err) {
      console.error("Error deleting lab:", err);
      toast.error("Failed to delete lab");
    }
  };

  return (
    <div className={classes.wrapper}>
      <ReusableTable
        title="Labs Profiles"
        columns={[
          { label: "Date & Time Entered", field: "dateTime" },
          { label: "Laboratory Name", field: "labName" },
          { label: "HPCZ License Number", field: "license" },
          { label: "Physical Address", field: "address" },
          { label: "Facility Phone", field: "phone" },
          { label: "Facility Email", field: "email" },
        ]}
        data={labData}
        onAdd={
          userRole === "OWNER" || userRole === "MINISTRY"
            ? () => navigate("/laboratory-mapping/profile/edit-profile")
            : undefined
        }
        showTopAdd={
          (userRole === "OWNER" && labData.length > 0) ||
          (userRole === "MINISTRY" && labData.length > 0)
        }
        onDelete={handleDelete} // ✅ Pass delete handler
      />
    </div>
  );
};

export default LabsProfiles;
